import { createStore } from "vuex";
import axios from "@/axios.js";
import endpoints from "@/endpoints.js";
import profile from "./modules/profile";
import account from "./modules/account";
import classical from "./ancestry/classical";
//import autosomal from "./ancestry/autosomal";
import createPersistedState from "vuex-persistedstate";
import myTestModule from "@/store/tests";
import { usePatientStore } from "@/store/health/blood";

// localStorage only holds strings, so a value that was never a real token comes back
// as "undefined" or "null" rather than as an absent key. Treat those as absent.
function isUsableToken(value) {
    return typeof value === "string" && value !== "" && value !== "undefined" && value !== "null";
}

function readStoredToken(key) {
    const value = localStorage.getItem(key);
    return isUsableToken(value) ? value : "";
}

const store = createStore({
    modules: {
        account,
        profile,
        classical,
        myTestModule,
        /* autosomal, */
    },
    plugins: [
        createPersistedState({
            reducer(val) {
                if (val.accessToken === null) {
                    return {};
                }
                return val;
            },
        }),
    ],
    state: {
        accessToken: readStoredToken("access_token"),
        refreshToken: readStoredToken("refresh_token"),
        loginCount: 0,
        referrer: localStorage.getItem("referrer") || null,
    },
    mutations: {
        setLoginCount(state, count) {
            state.loginCount = count;
        },
        setAccessToken(state, token) {
            // Guard against writing a non-token. localStorage stringifies whatever it
            // is given, so an undefined value lands as the string "undefined", which is
            // truthy and then fails to parse everywhere it is read.
            if (!isUsableToken(token)) {
                console.error("Refusing to store an invalid access token:", token);
                return;
            }
            state.accessToken = token;
            localStorage.setItem("access_token", token);
        },
        setRefreshToken(state, token) {
            state.refreshToken = token;
            localStorage.setItem("refresh_token", token);
        },
        setReferrer(state, referrer) {
            state.referrer = referrer;
            localStorage.setItem("referrer", referrer);
        },
        clearTokens(state) {
            state.accessToken = null;
            state.refreshToken = null;
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.clear();
        },
    },
    actions: {
        updateAccessToken({ commit }, token) {
            commit("setAccessToken", token);
        },
        updateRefreshToken({ commit }, token) {
            commit("setRefreshToken", token);
        },
        clearTokens({ commit }) {
            commit("clearTokens");
            // Signing out doesn't always reload the page (an expired session is sent to /signin
            // in place), and the kit list is only fetched when empty. Left alone, the next
            // account to sign in in this tab is shown the previous account's kits, and saves
            // them as its own.
            commit("CLEAR_TESTS_MODULE");
            commit("setAccount", null);
            commit("setProfile", {});
            commit("setAllProfiles", {});
        },
        setReferrer({ commit }, referrer) {
            if (referrer) {
                commit("setReferrer", referrer);
            }
        },
        async refreshToken({ commit, state }) {
            // Nothing to exchange: fail now, so the 401 handler ends the session, rather than
            // send a request the API can only reject.
            if (!isUsableToken(state.refreshToken)) {
                throw new Error("No refresh token to exchange");
            }

            // _retry: this goes through the same client whose 401 handler calls this action.
            // Unmarked, a rejected refresh started another refresh, and that one another, for as
            // long as the tab stayed open - thousands of requests an hour from one browser.
            const response = await axios.post(endpoints.refreshToken, {
                refresh_token: state.refreshToken,
            }, { _retry: true });

            // The API returns the JWT as `token` (Lexik's default response shape) --
            // the same field the login flow reads. Reading `access_token` here meant
            // every refresh silently stored undefined and ended the session.
            const token = response.data.token;
            if (!isUsableToken(token)) {
                throw new Error("Refresh response did not contain a token");
            }
            commit("setAccessToken", token);

            // Only relevant if refresh tokens are ever rotated server-side; harmless now.
            if (isUsableToken(response.data.refresh_token)) {
                commit("setRefreshToken", response.data.refresh_token);
            }
        },
        logout({ dispatch }) {
            dispatch("clearTokens");
            const patientStore = usePatientStore(); // Initialize the Pinia store
            patientStore.reset();
        },
    },
    getters: {
        getAccessToken: (state) => state.accessToken,
        getRefreshToken: (state) => state.refreshToken,
        isAuthenticated: (state) => !!state.accessToken,
        referrer: (state) => state.referrer,
    },
});

export default store;
